import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Autocomplete,
  Stack,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import BackToDashboard from "../common/BackToAdminDashboard";

const LANGS = [
  { key: "en", label: "English (en) *" },
  { key: "el", label: "Greek (el) *" },
  { key: "en_gb", label: "English UK (en_gb)" },
  { key: "en_us", label: "English US (en_us)" },
  { key: "es", label: "Spanish (es)" },
  { key: "fr", label: "French (fr)" },
  { key: "de", label: "German (de)" },
  { key: "it", label: "Italian (it)" },
  { key: "ro", label: "Romanian (ro)" },
  { key: "ru", label: "Russian (ru)" },
  { key: "sr", label: "Serbian (sr)" },
  { key: "sq", label: "Albanian (sq)" },
  { key: "bg", label: "Bulgarian (bg)" },
  { key: "hr", label: "Croatian (hr)" },
  { key: "sv", label: "Swedish (sv)" },
  { key: "ar", label: "Arabic (ar)" },
  { key: "ur", label: "Urdu (ur)" },
  { key: "zh", label: "Chinese (zh)" },
];

const emptyNames = () => Object.fromEntries(LANGS.map((l) => [l.key, ""]));

export default function AddGenericProduct() {
  const navigate = useNavigate();

  const [names, setNames] = useState(() => emptyNames());
  const [error, setError] = useState(null);

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    api.products
      .list()
      .then((resp) => setProducts(resp.data || []))
      .catch((err) => console.error("Failed to load products", err));
  }, []);

  const canSubmit = useMemo(() => {
    // απαιτούμε en + el (όπως και το backend)
    return (names.en || "").trim() && (names.el || "").trim();
  }, [names]);

  const handleNameChange = (langKey) => (e) => {
    const v = e.target.value;
    setNames((prev) => ({ ...prev, [langKey]: v }));
  };

  const fillEmptyFrom = (sourceKey) => {
    const src = (names[sourceKey] || "").trim();
    if (!src) return;
    setNames((prev) => {
      const next = { ...prev };
      for (const { key } of LANGS) {
        if (!next[key] || !next[key].trim()) next[key] = src;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!canSubmit) {
      setError("Please fill at least English (en) and Greek (el).");
      return;
    }

    try {
      const productIds = selectedProducts.map((p) => p.id);

      await api.genericProducts.add({
        names,
        product_ids: productIds,
      });

      setNames(emptyNames());
      setSelectedProducts([]);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add generic product");
    }
  };

  const getProductLabel = (p) => p.name_el || p.name_en || `Untitled (ID: ${p.id})`;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />

      <Typography variant="h4" gutterBottom>
        Add Generic Product (All Languages)
      </Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={() => fillEmptyFrom("en")}>
            Fill empty from EN
          </Button>
          <Button variant="outlined" onClick={() => fillEmptyFrom("el")}>
            Fill empty from EL
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          {LANGS.map((l) => (
            <TextField
              key={l.key}
              label={l.label}
              value={names[l.key]}
              onChange={handleNameChange(l.key)}
              required={l.key === "en" || l.key === "el"}
              fullWidth
            />
          ))}
        </Box>

        <Autocomplete
          multiple
          options={products}
          value={selectedProducts}
          onChange={(_, newValue) => setSelectedProducts(newValue)}
          getOptionLabel={getProductLabel}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {getProductLabel(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Link Products (optional)" placeholder="Type or scroll..." />
          )}
          sx={{ mt: 3 }}
        />

        <Button variant="contained" color="primary" type="submit" sx={{ mt: 3 }} disabled={!canSubmit}>
          Add
        </Button>
      </Box>
    </Container>
  );
}
